import { BookOpen, Flame, Users, Heart, Droplets, Sparkles, Cross as CrossIcon, Infinity as InfinityIcon } from "lucide-react";
import PageHeroBackground from "@/components/PageHeroBackground";

const BELIEFS = [
  {
    Icon: BookOpen,
    title: "The Bible",
    body: "The Bible is the inspired, infallible Word of God — our final authority for faith and life.",
  },
  {
    Icon: InfinityIcon,
    title: "The Trinity",
    body: "One God eternally existing in three persons — the Father, the Son, and the Holy Spirit — equal in essence, distinct in role.",
  },
  {
    Icon: Heart,
    title: "Jesus Christ",
    body: "Jesus is the Son of God who lived a sinless life, died on the cross, rose again, and is the only way to the Father.",
  },
  {
    Icon: CrossIcon,
    title: "Salvation",
    body: "Salvation is a free gift of God's grace through faith in Jesus Christ — repentance from sin and surrender to Him.",
  },
  {
    Icon: Droplets,
    title: "Baptism",
    body: "Water baptism by immersion is a public declaration of faith, following the command and example of Jesus.",
  },
  {
    Icon: Flame,
    title: "The Holy Spirit",
    body: "We believe in the baptism of the Holy Spirit with the evidence of speaking in other tongues, and in the ongoing gifts of the Spirit empowering every believer for life and ministry.",
  },
  {
    Icon: Users,
    title: "The Church",
    body: "God calls every believer to gather in the local church to worship, grow, serve, and reach the lost together.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative pt-40 pb-20 bg-[#FAFAF7] overflow-hidden" data-testid="about-hero">
        <PageHeroBackground opacity={0.22} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.28em] text-[#123F33] mb-6 font-semibold">Our Story</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#0B1D2E] leading-[1.05] tracking-tight">
              Built on the Gospel.
              <span className="block italic text-[#123F33]">For the people of DeLand.</span>
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-[#4A5562] leading-relaxed">
              The Potter's House is a local Pentecostal church with one calling — to faithfully preach and teach the message of Jesus Christ until every person in our city has had the chance to meet Him.
            </p>
          </div>
        </div>
      </section>

      {/* MISSION — SPLIT */}
      <section className="bg-white py-24" data-testid="about-mission-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="md:sticky md:top-32">
              <div className="text-xs uppercase tracking-[0.28em] text-[#123F33] font-semibold mb-4">Our Mission</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#0B1D2E] leading-tight">
                Go into all the world.
              </h2>
            </div>
          </div>
          <div className="md:col-span-7 space-y-6 text-lg text-[#4A5562] leading-relaxed">
            <p>
              The Potter's House Christian Church passionately follows the words of Jesus to go into all the world and preach the Gospel.
            </p>
            <p>
              We disciple people to discover their God-given calling and invest in couples and families, so together we can carry the Gospel into the whole world.
            </p>
            <p className="font-serif italic text-[#0B1D2E] text-xl border-l-2 border-[#123F33] pl-6">
              Come find what God has for you.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE BELIEVE */}
      <section className="bg-[#F3F2EB] py-28" data-testid="about-beliefs-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-2xl mb-16">
            <div className="text-xs uppercase tracking-[0.28em] text-[#123F33] mb-4 font-semibold">What We Believe</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#0B1D2E] leading-tight">
              A simple faith, deeply held.
            </h2>
            <p className="mt-6 text-lg text-[#4A5562] leading-relaxed">
              Our beliefs are rooted in the full Gospel — the same Gospel preached since the early church.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BELIEFS.map(({ Icon, title, body }, i) => (
              <div
                key={i}
                data-testid={`belief-${i}`}
                className="bg-white p-10 rounded-2xl border border-black/5 hover:-translate-y-1 hover:shadow-md transition-all duration-500"
              >
                <div className="w-11 h-11 rounded-full bg-[#123F33]/10 flex items-center justify-center mb-6">
                  <Icon className="w-5 h-5 text-[#123F33]" />
                </div>
                <h3 className="font-serif text-2xl text-[#0B1D2E] mb-3">{title}</h3>
                <p className="text-[#4A5562] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
